import React, { memo, useState, useMemo, useEffect } from 'react';
import classNames from 'classnames';
import Div from '../Div';
import Label from '../Label';
import { FieldError, FieldErrors } from 'react-hook-form';
import VALIDATION_ERRORS from '@/constants/validationErrors';
import ErrorIcon from '@/components/Icons/ErrorIcon';
import { UseFormRegister, FieldValues } from 'react-hook-form';
import Button from '../Button';
import Span from '../Span';
import InfoButtonTooltip from './InfoIconToolTip';
import is from '@/utils/is';
import { inputOnlyNumbers } from '@/utils/formValidation';

export enum InputTypes {
  text = 'text',
  password = 'password',
  email = 'email',
  number = 'number',
  tel = 'tel',
  checkbox = 'checkbox',
  radio = 'radio',
  textarea = 'textarea',
  select = 'select',
  heading = 'heading',
}

type InputFieldData = {
  className?: string;
  id?: string | undefined;
  label?: string;
  name: string;
  maxLength?: number;
  errors?: FieldErrors;
  value?: string | number | undefined;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onKeyPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  type?: InputTypes;
  dataTestId?: string;
  disabled?: boolean;
  required?: boolean;
  wrapperClassName?: string;
  data?: object;
  register?: UseFormRegister<FieldValues>;
  showConfirmButton?: boolean;
  onConfirm?: () => void;
  isButtonDisabled?: boolean;
  confirmButtonCondition?: (value: string | number | undefined) => boolean;
  showErrorMessage?: boolean;
  errorText?: string;
  textAreaHeight?: string;
  info?: string;
};

export type InputProps = {
  data: InputFieldData;
  className?: string;
  onChange?: (value: string) => void;
};

// eslint-disable-next-line react/display-name
const InputFieldBig = React.forwardRef(
  (
    {
      name,
      id,
      label,
      errors,
      disabled,
      type = InputTypes.text,
      dataTestId,
      value,
      showConfirmButton = false,
      onChange,
      onConfirm,
      isButtonDisabled,
      showErrorMessage,
      errorText,
      className,
      info,
      onBlur,
      onKeyDown,
      ...restProps
    }: InputFieldData,
    ref: React.Ref<HTMLInputElement> | React.Ref<HTMLTextAreaElement>,
  ) => {
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [inputValue, setInputValue] = useState<string | number | undefined>(value);

    useEffect(() => {
      setInputValue(value);
    }, [value]);

    const fieldError: FieldError | undefined = (errors?.[name] as FieldError) ?? undefined;
    const errorMessage = fieldError?.message || VALIDATION_ERRORS[fieldError?.type];

    const inputClasses = useMemo(
      () =>
        classNames(
          'border z-20 border-neutral-100 text-black focus:outline-blue-300 bg-transparent transition-all rounded-cxs text-body-lg px-s5 pt-6 pb-3 w-full',
          {
            ['border-red-500 pr-11']: errorMessage,
          },
          {
            'border-neutral-50, text-cti-disabled': disabled,
          },
        ),
      [errorMessage, disabled],
    );

    const labelClasses = useMemo(
      () =>
        classNames(
          'absolute z-0 left-s5 transition-all duration-300 text-cti-tertiary text-body-lg',
          {
            'top-s3 !text-body-sm !text-cti-secondary': isFocused || !!inputValue,
            'top-5': !isFocused && !inputValue,
            '!text-cti-disabled': disabled,
          },
        ),
      [isFocused, inputValue, disabled],
    );

    const onChangeHandler = (e: any) => {
      setInputValue(e.target.value);
      if (onChange) onChange(e);
    };

    const onBlurHandler = (e: any) => {
      if (onBlur && is.function(onBlur)) {
        onBlur(e);
      }
      setIsFocused(false);
      setInputValue(e.target.value.trim());
    };

    return (
      <Div className={classNames('relative z-10', className)}>
        <Label htmlFor={id} className={labelClasses}>
          {label}
        </Label>
        <input
          data-testid={dataTestId}
          type={type}
          name={name}
          id={id}
          ref={ref as React.Ref<HTMLInputElement>}
          autoComplete="off"
          disabled={disabled}
          value={inputValue}
          className={classNames(inputClasses)}
          {...restProps}
          onFocus={() => setIsFocused(true)}
          onBlur={onBlurHandler}
          onChange={onChangeHandler}
          onKeyDown={type === 'number' && !onKeyDown ? inputOnlyNumbers : onKeyDown}
        />
        {showConfirmButton && (
          <Button
            onClick={onConfirm}
            disabled={isButtonDisabled}
            className={classNames(
              'absolute right-4  top-1/2 z-30  transform -translate-y-1/2 px-6 py-2 text-button-small',
              {
                'top-1/3': showErrorMessage,
              },
            )}
            theme="link"
          >
            Confirm
          </Button>
        )}

        {!errorMessage && info && (
          <Div className="absolute right-4  top-1/2 z-30  transform -translate-y-1/3">
            <InfoButtonTooltip tooltipText={info} />
          </Div>
        )}

        {showErrorMessage && (
          <Span className="text-cu-warning top-12 left-6 text-body-base ">{errorText}</Span>
        )}
        {errorMessage && (
          <Div className="absolute top-5 right-s5">
            <ErrorIcon />
          </Div>
        )}
        <Div
          className={classNames('text-body-base mt-s2 text-neutral-700', {
            'text-red-500': errorMessage,
          })}
        >
          {errorMessage}
        </Div>
      </Div>
    );
  },
);

export default memo(InputFieldBig);
